import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// API hook for the outage board ("Ausfall-Board"): report resources or whole
// entertainments as not playable, end outages and maintain LaserZone vest
// counts. Outages automatically open a P1 issue and create timeslot blockers.
const useOutage = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const request = useCallback(
    async ({ url, method = 'GET', body, toastMessage, silentCodes = [] }) => {
      try {
        return await sendRequest({
          url,
          method,
          body,
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        // Codes the caller handles with a specific toast get no generic one.
        if (toastMessage && !silentCodes.includes(err?.code)) toast.error(toastMessage);
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getOutages = useCallback(
    async () => request({ url: '/api/v1/outage', toastMessage: 'Fehler beim Laden der Ausfälle' }),
    [request]
  );

  const createOutage = useCallback(
    async payload =>
      request({
        url: '/api/v1/outage',
        method: 'POST',
        body: payload,
        toastMessage: 'Fehler beim Melden des Ausfalls',
        silentCodes: ['OUTAGE_ALREADY_ACTIVE'],
      }),
    [request]
  );

  const endOutage = useCallback(
    async outageId =>
      request({
        url: `/api/v1/outage/${outageId}/end`,
        method: 'POST',
        body: {},
        toastMessage: 'Fehler beim Beenden des Ausfalls',
      }),
    [request]
  );

  const updateVestCount = useCallback(
    async payload =>
      request({
        url: '/api/v1/outage/vestCount',
        method: 'POST',
        body: payload,
        toastMessage: 'Fehler beim Aktualisieren der Westen-Anzahl',
      }),
    [request]
  );

  return { isLoading, error, getOutages, createOutage, endOutage, updateVestCount };
};

export default useOutage;
